import PageLayout from "@components/PageLayout";
import {
  Heading,
  Text,
  List,
  ListItem,
  ListIcon,
  OrderedList,
  UnorderedList,
} from "@chakra-ui/react";
import Label from "@components/Label";
import Link from "@components/Link";
import { Check } from "@phosphor-icons/react";

const Privacy = () => {
  return (
    <PageLayout>
      <Heading>Privacy at Retro</Heading>
      <Label>Last updated 12th December 2021</Label>
      <Text mt={4}>
        We understand the importance of transparent privacy for apps you use at
        work. This is why this page is as straight-forward as possible, showing
        you information in a clear manner while also allowing us to stay
        compliant with the relevant guidelines.
      </Text>
      <Heading my={4} fontSize="2xl">
        Who we are
      </Heading>
      <Text>
        This website is operated by Illumi Media, as authorised by the HMRC that
        encompasses RetroPlan and other sister brands.
        <br /> <br />
        We collect, use and are responsible for certain personal information
        about you. When we do so we are regulated under the General Data
        Protection Regulation which applies across the European Union and the
        United Kingdom and we are responsible as ‘processor’ of that personal
        information for the purposes of those laws.
      </Text>

      <Heading my={4} fontSize="2xl">
        Website
      </Heading>
      <Text>
        This privacy policy relates to <strong>your use</strong> of our website:
        retroplan.io (and other affiliate domains) only. Throughout our website
        we may link to other websites owned and operated by certain trusted
        third parties to provide users with support features and analytics for
        our website. These other third party websites may also gather
        information about you in accordance with their own separate privacy
        policies. For privacy information relating to these other third party
        websites, please consult their privacy policies as appropriate.
      </Text>

      <Heading my={4} fontSize="2xl">
        Data
      </Heading>
      <Text>
        Here is a summary of how data works at Retro:
        <List spacing={2} mt={5} mb={10}>
          <ListItem>
            <ListIcon as={Check} color="green.500" />
            Your data is stored on Google Firestore (europe-west1)
          </ListItem>
          <ListItem>
            <ListIcon as={Check} color="green.500" />
            We use the same tech Google uses for authentication
          </ListItem>
          <ListItem>
            <ListIcon as={Check} color="green.500" />
            Access validation is done both on APIs and the Database
          </ListItem>
          <ListItem>
            <ListIcon as={Check} color="green.500" />
            Your user data (such as email) is not accessible by any other user
          </ListItem>
          <ListItem>
            <ListIcon as={Check} color="green.500" />
            Data is encrypted at rest using 8 rounds of SCRYPT
          </ListItem>
        </List>
      </Text>

      <Heading my={4} fontSize="2xl">
        Collection and use of your data
      </Heading>

      <Text>
        We collect personal information from you either directly, such as when
        you register with us or contact us or indirectly, such as your browsing
        activity while on our website (see ‘Cookies’ below), or if such is
        contained in any of your Notes. The personal information we collect
        about you depends on the particular activities carried out through our
        website. This information includes: your name and email address We use
        this personal information to: create and manage your account with us
        provide goods and services to you notify you of any changes to our
        website or to our services that may affect you improve our services This
        website is not intended for use by children and we do not knowingly
        collect or use personal information relating to children.
      </Text>
      <Heading my={4} fontSize="2xl">
        Legal basis for processing your data
      </Heading>
      <Text>
        When we use your personal information we are required to have a legal
        basis for doing so. <br />
        <br /> There are various different legal bases on which we may rely,
        depending on what personal information we process and why. The legal
        bases we may rely on include:
        <List spacing={2} mt={5} mb={10}>
          <ListItem>
            <ListIcon as={Check} color="green.500" />
            Where you have given us clear consent for us to process your
            personal information
          </ListItem>
          <ListItem>
            <ListIcon as={Check} color="green.500" />
            Where it is necessary for a contract we have with you
          </ListItem>
          <ListItem>
            <ListIcon as={Check} color="green.500" />
            Where it is is necessary for us to comply with the law
          </ListItem>
          <ListItem>
            <ListIcon as={Check} color="green.500" />
            Where it is is necessary to protect you or someone else’s life
          </ListItem>

          <ListItem>
            <ListIcon as={Check} color="green.500" />
            Where it is is necessary for us to perform a task in the public
            interest
          </ListItem>
          <ListItem>
            <ListIcon as={Check} color="green.500" />
            Where it is is necessary for our legitimate interests
          </ListItem>
        </List>
      </Text>
      <Heading my={4} fontSize="2xl">
        Who we share your data with
      </Heading>
      <Text>
        We utilise tools such as{" "}
        <Link href="https://policies.google.com/privacy" target="_blank">
          Google Analytics
        </Link>
        ,{" "}
        <Link
          href="https://www.hotjar.com/legal/policies/privacy/"
          target="_blank"
        >
          HotJar
        </Link>{" "}
        and{" "}
        <Link href="https://www.intercom.com/legal/privacy" target="_blank">
          Intercom
        </Link>{" "}
        to help us analyse user behaviour and improve our service. Please refer
        to their respective privacy policies for how they handle your data. In
        terms of cold, hard storage we utilise{" "}
        <Link
          href="https://firebase.google.com/support/privacy"
          target="_blank"
        >
          Google Firestore
        </Link>{" "}
        in the Europe West (London) region. <br />
        <br />
        Some of these third party recipients may be based outside the United
        Kingdom and European Economic Area.
      </Text>
      <Heading my={4} fontSize="2xl">
        Transfer of your data out of the UK and EEA
      </Heading>
      <Text>
        We may transfer your personal information to the following which are
        located outside the United Kingdom (UK) and European Economic Area (EEA)
        as follows: USA in order to utilise the Intercom service which provides
        our in-tool support feature.
        <br /> <br />
        Any transfer of your personal information will be subject to equivalent
        safeguards as permitted under GDPR Article 46(5) of the General Data
        Protection Regulation) that are designed to help safeguard your privacy
        rights and give you remedies in the unlikely event of a misuse of your
        personal information.
        <br /> <br />
        To obtain a copy of such safeguards see here:
        <Link
          href="https://www.intercom.com/help/en/articles/1385437-how-intercom-complies-with-gdpr"
          target="_blank"
        >
          https://www.intercom.com/help/en/articles/1385437-how-intercom-complies-with-gdpr
        </Link>
        <br />
        <br />
        See ‘Cookies and other tracking & analytics technologies’ for details of
        the information submitted to Intercom. You may opt-out of having this
        information collected or submitted to Intercom; to do so please contact
        us at contact@retroplan.io.
        <br />
        <br /> If you would like further information please contact us (see ‘How
        to contact us’ below). We will not otherwise transfer your personal data
        outside of the UK OR area comprising the UK and EEA or to any
        organisation (or subordinate bodies) governed by public international
        law or which is set up under any agreement between two or more
        countries.
      </Text>
      <Heading my={4} fontSize="2xl">
        Cookies, tracking and analytics
      </Heading>
      <Text>
        We only use strictly necessary cookies within our service to maintain a
        users logged in session. Once this session expires or a user logs out
        then the cookie is removed. However, RetroPlan does utilise third party
        analytics tools (see below) and Google, GitHub and Twitter social login
        integration which also use cookies.
        <br /> <br />
        If you would like information on how they use cookies, please refer
        directly to their websites. Additionally, you can find further
        information on how third parties and your browser may use cookies here:{" "}
        <Link href="https://aboutcookies.org" target="_blank">
          aboutcookies.org
        </Link>
        . We use analytics tools to gather user information whilst they use the
        service.
      </Text>
      <Heading my={4} fontSize="2xl">
        Marketing
      </Heading>
      <Text>
        We would like to send you information about products and services, which
        may be of interest to you. <br /> <br /> Where we have your consent or
        it is in our legitimate interests to do so, we may do this by email. We
        will only ask whether you would like us to send you marketing messages
        when you tick the relevant boxes upon or after registration. If you have
        previously agreed to being contacted in this way, you can unsubscribe at
        any time by: contacting us at contact@retroplan.io or using the
        ‘unsubscribe’ link in emails.
      </Text>
      <Heading my={4} fontSize="2xl">
        Your rights
      </Heading>
      <Text>
        Under the General Data Protection Regulation you have a number of
        important rights free of charge.
        <br /> <br /> In summary, those include rights to:
        <List spacing={2} mt={5} mb={10}>
          <ListItem>
            <ListIcon as={Check} color="green.500" />
            Fair processing of information and transparency over how we use your
            use personal information
          </ListItem>

          <ListItem>
            <ListIcon as={Check} color="green.500" />
            Access to your personal information and to certain other
            supplementary information
          </ListItem>

          <ListItem>
            <ListIcon as={Check} color="green.500" />
            Receive the personal information concerning you
          </ListItem>

          <ListItem>
            <ListIcon as={Check} color="green.500" />
            Restrict our processing of your personal information in certain
            circumstances
          </ListItem>

          <ListItem>
            <ListIcon as={Check} color="green.500" />
            Claim compensation for damages caused by our breach of any data
            protection laws
          </ListItem>
        </List>
        For further information on each of those rights, including the
        circumstances in which they apply, see the Guidance from the UK
        Information Commissioner’s Office (ICO) on individuals rights under the
        General Data Protection Regulation. If you would like to exercise any of
        those rights, please: email us at contact@retroplan.io.
      </Text>
      <Heading my={4} fontSize="2xl">
        Keeping your data secure
      </Heading>
      <Text>
        We have appropriate security measures in place to prevent personal
        information from being accidentally lost, or used or accessed in an
        unauthorised way. <br /> <br /> We limit access to your personal
        information to those who have a genuine business need to know it. Those
        processing your information will do so only in an authorised manner and
        are subject to a duty of confidentiality. We also have procedures in
        place to deal with any suspected data security breach. We will notify
        you and any applicable regulator of a suspected data security breach
        where we are legally required to do so. You can read more about how we
        keep your personal information secure on our Security page.
      </Text>
      <Heading my={4} fontSize="2xl">
        How to complain
      </Heading>
      <Text>
        We hope that we can resolve any query or concern you raise about our use
        of your information - contact@retroplan.io <br /> <br />
        The General Data Protection Regulation also gives you the right to lodge
        a complaint with a supervisory authority, in particular in a European
        Economic Area state or in the United Kingdom if you work, normally live
        or if any alleged infringement of data protection laws occurred in the
        relevant state. The supervisory authority in the UK is the Information
        Commissioner who may be contacted at https://ico.org.uk/concerns/ or
        telephone: 0303 123 1113.
      </Text>
      <Heading my={4} fontSize="2xl">
        Changes to this website privacy policy
      </Heading>
      <Text>
        This website privacy policy was published on 11th December 2021 and the
        last updated date can be viewed at the top of the page. We may change
        this website privacy policy from time to time, when we do we will inform
        you via a pop up on service entry to reconfirm acceptance to the change
        in terms and policies.
      </Text>
      <Heading my={4} fontSize="2xl">
        Getting in touch
      </Heading>
      <Text>
        Please contact us at{" "}
        <Link href="mailto:contact@retroplan.io">contact@retroplan.io</Link> if
        you have any questions about this privacy notice or the information we
        hold about you.
      </Text>
    </PageLayout>
  );
};

export default Privacy;
